/**
 * Waiting on you (`/waiting`), the signed-in home: one inbox across every Team
 * the reader supervises.
 *
 * One group per Team (the Team's colour, its name, its count), and inside each
 * the asks addressed to the reader, oldest first: `isWaitingOnViewer`, the
 * same rule the rail's pill counts. A split's asks print under their parent's
 * row, which says whose ticket they are and counts as no ask (YAG-1113). A
 * row opens the same ticket sheet the Team queue does (`?item=`).
 *
 * Two older addresses land here too: `?review=<prStateId>` opens one pull
 * request's review over the inbox, and `?add=1` (from `/work/add`) opens Add
 * tickets for the first Team the reader is on. `&q=COREX-123` (the pick link
 * an unclaimed assignment replies with, `addTicketUrl`) searches that key at
 * once, and a reader on several Teams picks the Team in the sheet.
 */
import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuthSession } from "../hooks/useWorkspaceRole.js";
import { Group, Groups, ListHead } from "../queue/index.js";
import { SurfaceSkeleton } from "../ui/index.js";
import { deskAccentToken } from "../desks/deskAccent.js";
import { useDockBinding } from "../shell/askDockStore.js";
import { workScope } from "../shell/yagniRailScopes.js";
import { useWorkFeed } from "./useWorkFeed.js";
import { useWorkerLookup } from "./useWorkerLookup.js";
import { useTeamIdentity } from "./useTeamIdentity.js";
import { foldPipelineRows } from "./workViews.js";
import { ReviewCaseDialog } from "./review/ReviewCaseDialog.js";
import { waitingByTeam } from "../ticket/queueGroups.js";
import { TicketEntry } from "../ticket/TicketRow.js";
import { useQueueSheet } from "../ticket/useQueueSheet.js";
import { AddTicketsSheet } from "../ticket/AddTicketsSheet.js";
import { SystemNeedAsks, useSystemNeedAsks } from "./SystemNeedAsks.js";

/** "3 across 2 teams · oldest first inside each". */
export function waitingSubLine(count: number, teams: number): string {
  if (count === 0) return "Nothing waits on you";
  return `${count} across ${teams} ${teams === 1 ? "team" : "teams"} · oldest first inside each`;
}

export function WaitingRoute() {
  const viewerUserId = useAuthSession()?.user?.id ?? null;
  const feed = useWorkFeed();
  const workerOf = useWorkerLookup();
  const { roster, ready: rosterReady } = useTeamIdentity();
  const [params, setParams] = useSearchParams();
  const reviewParam = params.get("review");
  const now = Date.now();
  useDockBinding({ scope: workScope() });

  const active = useMemo(() => feed.items.filter((item) => !item.archivedAt), [feed.items]);
  const rows = useMemo(() => foldPipelineRows(active), [active]);
  const deskIds = useMemo(() => roster.map((team) => team.id), [roster]);
  const groups = useMemo(
    () => waitingByTeam(rows, deskIds, viewerUserId),
    [rows, deskIds, viewerUserId],
  );
  const order = useMemo(
    () => groups.flatMap((group) => group.rows.map((row) => row.anchor.id)),
    [groups],
  );
  const count = order.length;
  const systemNeeds = useSystemNeedAsks();

  // Add tickets from /work/add or a pick link (`?add=1&q=COREX-123`): the
  // first Team the reader is on, with a Team select when they are on several.
  const memberTeams = useMemo(() => roster.filter((team) => team.viewerIsMember), [roster]);
  const addTeam = memberTeams[0] ?? null;
  const addKey = params.get("q")?.trim() || undefined;
  const sheet = useQueueSheet({
    items: active,
    nextAfter: (id) => {
      const at = order.indexOf(id);
      const rest = order.filter((entry) => entry !== id);
      if (rest.length === 0) return null;
      return order.slice(at + 1).find((entry) => entry !== id) ?? rest[0] ?? null;
    },
    renderAdd: addTeam
      ? (close, openItem) => (
          <AddTicketsSheet
            deskId={addTeam.id}
            teamName={addTeam.name}
            source={null}
            filter={null}
            picked={addTeam.pickedToday}
            initialQuery={addKey}
            teams={memberTeams.map((team) => ({
              id: team.id,
              name: team.name,
              pickedToday: team.pickedToday,
            }))}
            onAdded={(first) => (first ? openItem(first) : close())}
            onClose={close}
          />
        )
      : undefined,
  });
  const addWithoutTeam = params.get("add") === "1" && rosterReady && !addTeam;

  if (!feed.ready)
    return <SurfaceSkeleton label="Gathering" lens="work" testId="waiting-loading" />;

  const nameOf = (deskId: string) => roster.find((team) => team.id === deskId)?.name ?? "This team";

  return (
    <>
      <ListHead title="Waiting on you" sub={[waitingSubLine(count, groups.length)]} />
      <Groups>
        {addWithoutTeam && (
          <div className="empty dashed tight" data-testid="waiting-add-hint">
            <p>
              Add tickets from a Team's queue. You are not on a Team yet; add one from the rail.
            </p>
          </div>
        )}
        {feed.error && active.length === 0 && (
          <div className="empty dashed tight" role="alert">
            <p>{feed.error}</p>
          </div>
        )}
        {!(feed.error && active.length === 0) && count === 0 && systemNeeds.asks.length === 0 && (
          <div className="empty dashed" data-testid="waiting-empty">
            <h3>Nothing waits on you</h3>
            <p>Every Team is either working or idle. New asks land here, oldest first.</p>
          </div>
        )}
        {count > 0 && (
          <div data-testid="waiting-list">
            {groups.map((group) => (
              <Group
                key={group.deskId}
                label={nameOf(group.deskId)}
                tone="needs"
                count={group.rows.length}
                dot={deskAccentToken(group.deskId)}
                plain
              >
                {group.heads.map((row) => (
                  <TicketEntry
                    key={row.anchor.id}
                    row={row}
                    group="needs"
                    parts={group.parts}
                    workerOf={workerOf}
                    viewerUserId={viewerUserId}
                    now={now}
                    isSelected={(each) => sheet.selectedId === each.anchor.id}
                    onOpen={(id) => sheet.openItem(id)}
                  />
                ))}
              </Group>
            ))}
          </div>
        )}
        <SystemNeedAsks
          asks={systemNeeds.asks}
          failed={systemNeeds.failed}
          reload={systemNeeds.reload}
          teamName={nameOf}
        />
      </Groups>
      {reviewParam && (
        <ReviewCaseDialog
          prStateId={reviewParam}
          onClose={() =>
            setParams(
              (prev) => {
                const next = new URLSearchParams(prev);
                next.delete("review");
                return next;
              },
              { replace: true },
            )
          }
        />
      )}
    </>
  );
}
